import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { StandardDropdown, EmptyState } from '../../../../../component/common/StandardUI';
import { CompanyApi } from '../../../../../api/companyApi';
import { errorToast, successToast, getLocalStorageItem } from '../../../../../utils/helper';
import Loader from '../../../../../component/common/Loader';
import Topbar from '../../../../PulseSurvey/Topbar';
import Breadcrumb from '../../../../../component/common/Breadcrumb';
import CommentsPanel from '../components/CommentsPanel';
import uploadTrayIcon from '../../../../../assets/svg/icons/Upload-Tray--Streamline-Flex.svg';
import fileReportIcon from '../../../../../assets/svg/icons/File-Report--Streamline-Flex.svg';

const ALLOWED_EXTENSIONS = [
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'csv',
  'png',
  'jpg',
  'jpeg'
];
const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;

const validateFile = (file) => {
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (!ALLOWED_EXTENSIONS.includes(extension)) {
    return `${file.name}: file type not supported (allowed: ${ALLOWED_EXTENSIONS.join(', ')}).`;
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return `${file.name}: file exceeds the 25MB size limit.`;
  }
  return null;
};

const DOC_TYPE_FILTERS = [
  { label: 'All types', value: '', extensions: [] },
  { label: 'PDF', value: 'pdf', extensions: ['pdf'] },
  { label: 'Word (DOC/DOCX)', value: 'doc', extensions: ['doc', 'docx'] },
  { label: 'Excel (XLS/XLSX)', value: 'xls', extensions: ['xls', 'xlsx'] },
  { label: 'PowerPoint (PPT/PPTX)', value: 'ppt', extensions: ['ppt', 'pptx'] },
  { label: 'CSV', value: 'csv', extensions: ['csv'] },
  { label: 'Images', value: 'image', extensions: ['png', 'jpg', 'jpeg'] }
];

const getExtension = (fileName = '') => fileName.split('.').pop()?.toLowerCase();

const DocumentFolderDetail = () => {
  const { folderId } = useParams();
  const userData = JSON.parse(getLocalStorageItem('userData'));
  const slug = userData?.slug;

  const [folder, setFolder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [previewingId, setPreviewingId] = useState(null);
  const [typeFilter, setTypeFilter] = useState('');
  const fileInputRef = useRef(null);
  const uploading = uploadQueue.some((item) => item.status === 'uploading');

  const fetchFolder = async () => {
    try {
      setLoading(true);
      const response = await CompanyApi.getDocumentFolderById(folderId);
      if (response?.data?.meta?.code === 1) {
        setFolder(response?.data?.data);
      } else {
        errorToast(response?.data?.meta?.message || 'Failed to load folder');
      }
    } catch (error) {
      errorToast(error.message || 'Failed to load folder');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFolder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderId]);

  const uploadFile = async (file) => {
    const queueId = `${Date.now()}-${file.name}`;
    setUploadQueue((prev) => [
      ...prev,
      { id: queueId, name: file.name, progress: 0, status: 'uploading' }
    ]);

    const setProgress = (progress) =>
      setUploadQueue((prev) =>
        prev.map((item) => (item.id === queueId ? { ...item, progress } : item))
      );
    const setStatus = (status) =>
      setUploadQueue((prev) =>
        prev.map((item) => (item.id === queueId ? { ...item, status } : item))
      );

    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await CompanyApi.uploadDocumentFile(folderId, formData, (event) => {
        if (event.total) setProgress(Math.round((event.loaded / event.total) * 100));
      });
      if (response?.data?.meta?.code === 1) {
        setStatus('done');
        successToast(response?.data?.meta?.message || `${file.name} uploaded successfully`);
        fetchFolder();
      } else {
        setStatus('error');
        errorToast(response?.data?.meta?.message || `Failed to upload ${file.name}`);
      }
    } catch (error) {
      setStatus('error');
      errorToast(error.message || `Failed to upload ${file.name}`);
    } finally {
      setTimeout(() => {
        setUploadQueue((prev) => prev.filter((item) => item.id !== queueId));
      }, 2000);
    }
  };

  const handleFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    const validFiles = [];
    files.forEach((file) => {
      const error = validateFile(file);
      if (error) {
        errorToast(error);
      } else {
        validFiles.push(file);
      }
    });

    validFiles.forEach((file) => uploadFile(file));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handlePreview = async (file) => {
    try {
      setPreviewingId(file._id);
      const response = await CompanyApi.getDocumentFileById(folderId, file._id);
      const previewUrl = response?.data?.data?.fileUrl || file.fileUrl;
      window.open(previewUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      errorToast(error.message || 'Failed to open file');
    } finally {
      setPreviewingId(null);
    }
  };

  const handleAddComment = async (text) => {
    try {
      setCommentSubmitting(true);
      const response = await CompanyApi.addDocumentFolderComment(folderId, { text });
      if (response?.data?.meta?.code === 1) {
        fetchFolder();
      } else {
        errorToast(response?.data?.meta?.message || 'Failed to add comment');
      }
    } catch (error) {
      errorToast(error.message || 'Failed to add comment');
    } finally {
      setCommentSubmitting(false);
    }
  };

  const pages = [
    { name: 'People plan', href: `/${slug}/people-plan`, current: false },
    { name: 'Document Hub', href: `/${slug}/people-plan/document-hub`, current: false },
    { name: folder?.name || 'Folder', href: '#', current: true }
  ];

  if (loading && !folder) {
    return (
      <div className="relative lg:mb-[45px]">
        <Loader />
      </div>
    );
  }

  if (!folder) return null;

  const activeFilter = DOC_TYPE_FILTERS.find((option) => option.value === typeFilter);
  const filteredDocuments = (folder.documents || []).filter(
    (file) =>
      !activeFilter?.extensions.length ||
      activeFilter.extensions.includes(getExtension(file.fileName))
  );

  return (
    <div className="relative lg:mb-[45px]">
      {loading && <Loader />}
      <Helmet>
        <meta charSet="utf-8" />
        <title>Document hub</title>
      </Helmet>
      <Topbar title="Document hub" infoBtn={false} />
      <Breadcrumb pageList={pages} className="lg:mt-8" />

      <div className="mt-6">
        <div className="space-y-6">
          <div className="rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder p-6">
            <h3 className="text-base font-medium text-gray-700 dark:text-gray-200">
              Folder: {folder.name}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-5">
              {folder.createdBy?.name ? `Created by: ${folder.createdBy.name}` : ''}
              {folder.createdBy?.name && folder.updatedAt ? ' | ' : ''}
              {folder.updatedAt
                ? `Last updated: ${moment(folder.updatedAt).format('MMMM Do, hh:mma')}`
                : ''}
            </p>

            <div
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragging(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragging(false);
              }}
              onDrop={handleDrop}
              onClick={() => !uploading && fileInputRef.current?.click()}
              className={`flex flex-col items-center justify-center text-center cursor-pointer rounded-input border py-10 mb-6 transition-colors ${dragging
                  ? 'border-shoorah-primary bg-shoorah-primary/5'
                  : 'border-shoorah-borderGray dark:border-shoorah-darkBorder bg-shoorah-normanGray dark:bg-shoorah-darkBgColor'
                }`}
            >
              <img src={uploadTrayIcon} alt="" className="w-7 h-7 mb-2 dark:invert" />
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                disabled={uploading}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  handleFiles(e.target.files);
                  e.target.value = '';
                }}
              />
              <p className="text-sm text-gray-700 dark:text-gray-200">
                Choose a file or drag it here
              </p>
            </div>

            {uploadQueue.length > 0 && (
              <div className="space-y-2 mb-5">
                {uploadQueue.map((item) => (
                  <div key={item.id} className="text-xs">
                    <div className="flex justify-between mb-1">
                      <span className="text-gray-600 dark:text-gray-300 truncate">{item.name}</span>
                      <span className="text-gray-400">
                        {item.status === 'error' ? 'Failed' : `${item.progress}%`}
                      </span>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-shoorah-bodyBackground dark:bg-shoorah-darkBgColor overflow-hidden">
                      <div
                        className={`h-full rounded-full ${item.status === 'error' ? 'bg-red-500' : 'bg-shoorah-active'
                          }`}
                        style={{ width: `${item.status === 'error' ? 100 : item.progress}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center gap-4 mb-3">
              <h3 className="text-base font-medium text-gray-700 dark:text-gray-200">Documents</h3>
              <StandardDropdown
                label="Filter"
                options={DOC_TYPE_FILTERS}
                value={typeFilter}
                onChange={setTypeFilter}
              />
            </div>

            {filteredDocuments.length === 0 && (
              <EmptyState
                title={typeFilter ? 'No files match this filter' : 'No files uploaded yet'}
                description={
                  typeFilter
                    ? 'Try a different file type or clear the filter.'
                    : 'Upload a file using the box above to get started.'
                }
              />
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2">
              {filteredDocuments.map((file) => (
                <button
                  key={file._id}
                  type="button"
                  onClick={() => handlePreview(file)}
                  disabled={previewingId === file._id}
                  className="text-left flex items-center gap-3 border-b border-gray-100 dark:border-shoorah-darkBorder py-3 sm:odd:pr-8 sm:even:pl-8 sm:even:border-l hover:text-shoorah-primary transition-colors disabled:opacity-60"
                >
                  <img src={fileReportIcon} alt="" className="w-6 h-6 shrink-0 dark:invert" />
                  <span className="text-sm text-gray-700 dark:text-gray-200 truncate">
                    {file.fileName}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder p-6">
            <CommentsPanel
              comments={folder.comments || []}
              onAddComment={handleAddComment}
              submitting={commentSubmitting}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentFolderDetail;
